import { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from '@store/timetable/dayjs.config';
import relativeTime from 'dayjs/plugin/relativeTime';
import { declOfNum } from '@/helpers/declOfNum';
import { useIsLimitTheTimeForEnteringLesson } from '@pages/Admin/PlatformSettings2/ScheduleClasses/hooks/useIsLimitTheTimeForEnteringLesson';
import store from '@store/redux.store';
import { useSelector } from 'react-redux';

dayjs.extend(relativeTime);

const getMeetingTimeInfo = ({date_conference, start_time, timeLimit}) => {
    const platformTime = dayjs(store.getState().platformTime?.platformTime);
    const meetingDateTime = dayjs(`${date_conference} ${start_time}`);
    
    // Разница в минутах (может быть отрицательной, если встреча уже прошла)
    const diffInMinutes = meetingDateTime.diff(platformTime, 'minute');
    // Если до встречи timeLimit минут или меньше - активируем кнопку
    const isActive = diffInMinutes <= Number(timeLimit);
    // Если время встречи уже прошло
    const hasMeetingPassed = meetingDateTime.isBefore(platformTime);

    return {
        diffInMinutes,
        isActive: isActive || hasMeetingPassed,
        hasMeetingPassed,
    };
};

export const useMeetingTimeRemaining = ({date_conference, start_time}) => {
    const { timeLimit } = useIsLimitTheTimeForEnteringLesson();
    const platformTime = useSelector(store => store.platformTime?.platformTime);
    // Состояние для хранения информации о том, активно ли время встречи
    const [isMeetingTimeActive, setIsMeetingTimeActive] = useState(false);
    // Состояние для хранения оставшегося времени в минутах
    const [timeRemaining, setTimeRemaining] = useState(null);
    // Ref для хранения интервала
    const intervalRef = useRef(null);

    // Функция проверки времени до встречи
    const checkMeetingTime = useCallback(() => {
        if (!date_conference || !start_time) {
            setIsMeetingTimeActive(false);
            setTimeRemaining(null);
            return false;
        }

        const { diffInMinutes, hasMeetingPassed, isActive } = getMeetingTimeInfo({date_conference, start_time, timeLimit})
        
        // Обновляем оставшееся время
        setTimeRemaining(diffInMinutes);
       
        setIsMeetingTimeActive(isActive);
        
        return isActive || hasMeetingPassed;
    }, [date_conference, start_time, timeLimit, platformTime]);

    // Запускаем проверку при изменении даты и времени встречи
    useEffect(() => {
        if (date_conference && start_time) {
            // Выполняем начальную проверку
            checkMeetingTime();
            
            // Запускаем интервал для проверки каждую минуту
            intervalRef.current = setInterval(() => {
                checkMeetingTime();
            }, 60000); // 60000 мс = 1 минута
            
            // Очищаем интервал при размонтировании компонента или изменении зависимостей
            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        } else {
            // Если нет данных, сбрасываем состояния
            setIsMeetingTimeActive(false);
            setTimeRemaining(null);
        }
    }, [date_conference, start_time, timeLimit, platformTime]); 

    // Функция для форматирования времени
    const formatTimeRemaining = useCallback(() => {
        if (timeRemaining === null) return null;
        
        if (timeRemaining < 0) {
            return "Встреча уже началась";
        } else if (timeRemaining === 0) {
            return "Встреча начинается сейчас";
        } else if (timeRemaining < 60) {
            return `До начала встречи: ${timeRemaining} мин.`;
        } else if (timeRemaining < 24 * 60) {
            const hours = Math.floor(timeRemaining / 60);
            const minutes = timeRemaining % 60;
            const hourWord = `${declOfNum(hours, ['час', 'часа', 'часов'])}`;
            if (minutes > 0) {
                const minuteWord = `${declOfNum(minutes, ['минута', 'минуты', 'минут'])}`;
                return `До начала встречи: ${hours} ${hourWord} ${minutes} ${minuteWord}`;
            }
            return `До начала встречи: ${hours} ${hourWord}`;
        } else {
            const meetingDateTime = dayjs(`${date_conference} ${start_time}`);
            return `До начала встречи: ${meetingDateTime.fromNow(true)}`;
        }
    }, [timeRemaining, date_conference, start_time]);

    // Функция для остановки интервала (например, после создания встречи)
    const stopInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    return {
        isMeetingTimeActive,
        timeRemaining,
        formatTimeRemaining,
        stopInterval,
        checkMeetingTime
    };
};